import { useLayoutEffect, useMemo, useRef } from 'react';
import { getData, OAuthError } from '../backend';
import { useModal } from '../contexts';
import Modal from './Modal';
import useSWR, { KeyedMutator } from "swr";
import hsObjectTypes, { HubspotObject, HubspotObjectList, HubspotObjectListKey } from '../hsObjectTypes';
import ProLabel from './ProLabel';
import useUser from '../hooks/useUser';
import PayWallModalPro from './PayWallModalPro';
import OAuthModalHubspot from './OAuthModalHubspot';
import WorkflowModalHubspotPropertiesDetail from './WorkflowModalHubspotPropertiesDetail';
import { Workflow } from '../interfaces';

interface Props {
  workflow: Workflow;
  refreshWorkflow: KeyedMutator<Workflow>;
  onClose: () => void;
  captureProperties?: boolean;
}

function WorkflowModalHubspotPropertiesList(props: Props) {
  const { user } = useUser();
  const { openModal, closeModal } = useModal();

  const { data, isLoading, error } = useSWR<Array<HubspotObjectList>>(`/hubspot/object-types/`, getData, { fallbackData: hsObjectTypes, shouldRetryOnError: false });

  const scollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    // Hack to set modal back to top after it has been previously loaded
    setTimeout(() => scollRef.current?.scrollTo({ top: 0, behavior: "auto" }), 0)
  }, [scollRef]);

  const objectListMap: Record<HubspotObjectListKey, Array<HubspotObject>> = useMemo(() => {
    let map: Record<HubspotObjectListKey, Array<HubspotObject>> = {
      "PRIMARY": [],
      "SECONDARY_CONTACT": [],
      "SECONDARY_DEAL": [],
      "OBJECT_LIBRARY": [],
      "CUSTOM_OBJECTS": [],
    };

    for (const objectList of (data || []))
      map[objectList.key] = objectList.objects

    return map;
  }, [data]);

  const selectedPropertiesAmount: Record<string, number> = useMemo(() => {
    let map: Record<string, number> = {};

    const config = props.workflow.source?.hubspotConfig;
    const properties = props.captureProperties ? config?.hubspotCaptureProperties : config?.hubspotProperties;

    for (const field of (properties || [])) {
      const [objectId,] = field.split(".", 1);
      map[objectId] = (map[objectId] ?? 0) + 1;
    }

    return map;
  }, [props.workflow.source?.hubspotConfig, props.captureProperties]);

  function handleSelectObject(objectId: string, isCustomObject: boolean = false) {
    if (isCustomObject && !user?.subscription?.featureHubspotCustomObjects)
      return openModal(<PayWallModalPro />);

    openModal(<WorkflowModalHubspotPropertiesDetail objectTypeId={objectId} workflow={props.workflow} refreshWorkflow={props.refreshWorkflow} onClose={props.onClose} captureProperties={props.captureProperties} />)
  }

  return (
    <Modal
      title={"HubSpot Properties"}
      subtitle={"Select the HubSpot object you want to select properties from."}
      secondaryButtonProps={{ text: "Cancel", onClick: closeModal }}
      onClose={props.onClose}
    >
      <div ref={scollRef} className='flex flex-col overflow-y-scroll h-[400px] gap-2 pr-8'>

        <div className='flex-shrink-0 font-semibold text-gray-600'>
          Standard Objects
        </div>
        {objectListMap["PRIMARY"].map((object) =>
          <div key={object.id} className='flex border rounded w-full p-4 hover:border-blue cursor-pointer font-gilroy font-semibold text-gray-600 flex-shrink-0' onClick={() => handleSelectObject(object.id)}>
            {object.name}
            {selectedPropertiesAmount[object.id] > 0 &&
              <div className='flex items-center justify-center bg-gray-100 rounded-full h-[20px] px-2 ml-auto text-xs text-gray-600 font-semibold'>
                Selected: {selectedPropertiesAmount[object.id]}
              </div>
            }
          </div>
        )}

        <div className='flex items-center flex-shrink-0 font-semibold text-gray-600 mt-4'>
          Custom Objects
          <ProLabel />
        </div>
        {isLoading &&
          <div className='flex-shrink-0 font-semibold text-gray-400 text-sm -mt-2'>
            Loading<span className='loading-ellipse' />
          </div>
        }
        {(error instanceof OAuthError) &&
          <div className='flex flex-col items-start'>
            <div className='flex-shrink-0 font-semibold text-gray-400 text-sm -mt-2'>
              Connect HubSpot to Portant to fetch custom objects
            </div>
            <button className='btn btn-white text-sm py-1 px-2 ' onClick={() => openModal(<OAuthModalHubspot oauthUrl={error.oauthUrl} onClose={() => openModal(<WorkflowModalHubspotPropertiesList {...props} />)} />)}>
              Connect HubSpot
            </button>
          </div>
        }
        {objectListMap["CUSTOM_OBJECTS"].map((object) =>
          <div key={object.id} className='flex border rounded w-full p-4 hover:border-blue cursor-pointer font-gilroy font-semibold text-gray-600 flex-shrink-0' onClick={() => handleSelectObject(object.id, true)}>
            {object.name}
            {selectedPropertiesAmount[object.id] > 0 &&
              <div className='flex items-center justify-center bg-gray-100 rounded-full h-[20px] px-2 ml-auto text-xs text-gray-600 font-semibold'>
                Selected: {selectedPropertiesAmount[object.id]}
              </div>
            }
          </div>
        )}

        <div className='flex-shrink-0 font-semibold text-gray-600 mt-4'>
          Object Library
        </div>
        <div className='flex-shrink-0 font-semibold text-gray-400 text-sm -mt-2'>
          These object types must be enabled in your Hubspot Portal in order to be used. <a className='underline hover:text-blue' href='https://knowledge.hubspot.com/object-settings/use-the-object-library' target='_blank' rel="noreferrer">Learn more</a>
        </div>
        {objectListMap["OBJECT_LIBRARY"].map((object) =>
          <div key={object.id} className={`flex border rounded w-full p-4 font-gilroy font-semibold text-gray-600 flex-shrink-0 ${object.enabled ? "hover:border-blue cursor-pointer" : "bg-gray-100"}`} onClick={() => object.enabled ? handleSelectObject(object.id) : undefined}>
            {object.name}
            {selectedPropertiesAmount[object.id] > 0 &&
              <div className='flex items-center justify-center bg-gray-100 rounded-full h-[20px] px-2 ml-auto text-xs text-gray-600 font-semibold'>
                Selected: {selectedPropertiesAmount[object.id]}
              </div>
            }
            {!object.enabled &&
              <div className='bg-gray-200 font-semibold ml-auto text-gray-400 text-xs px-2 py-1 rounded'>
                Disabled
              </div>
            }
          </div>
        )}

        <div className='flex-shrink-0 font-semibold text-gray-600 mt-4'>
          Deal Related Objects
        </div>
        {objectListMap["SECONDARY_DEAL"].map((object) =>
          <div key={object.id} className='flex border rounded w-full p-4 hover:border-blue cursor-pointer font-gilroy font-semibold text-gray-600 flex-shrink-0' onClick={() => handleSelectObject(object.id)}>
            {object.name}
            {selectedPropertiesAmount[object.id] > 0 &&
              <div className='flex items-center justify-center bg-gray-100 rounded-full h-[20px] px-2 ml-auto text-xs text-gray-600 font-semibold'>
                Selected: {selectedPropertiesAmount[object.id]}
              </div>
            }
          </div>
        )}

        <div className='flex-shrink-0 font-semibold text-gray-600 mt-4'>
          Contact Related Objects
        </div>
        {objectListMap["SECONDARY_CONTACT"].map((object) =>
          <div key={object.id} className='flex border rounded w-full p-4 hover:border-blue cursor-pointer font-gilroy font-semibold text-gray-600 flex-shrink-0' onClick={() => handleSelectObject(object.id)}>
            {object.name}
            {selectedPropertiesAmount[object.id] > 0 &&
              <div className='flex items-center justify-center bg-blue rounded-full h-[20px] min-w-[20px] ml-auto text-xs text-white font-semibold'>
                {selectedPropertiesAmount[object.id]}
              </div>
            }
          </div>
        )}
      </div>
    </Modal>
  );
}

export default WorkflowModalHubspotPropertiesList;
